import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Descriptions, Input, DatePicker, Select, Row, Col, Tooltip, Badge } from 'antd';
import { EyeOutlined, ReloadOutlined, SearchOutlined, FilterOutlined, DownloadOutlined, HistoryOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  UserAddOutlined, DollarOutlined, WalletOutlined, CreditCardOutlined, FileSearchOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { testApi } from '../services/api';
import { getChannelLogo, channelLogos, buildChannelOptions } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;

export default function ExecutionHistory() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);
  const [filters, setFilters] = useState({
    channelCode: '',
    testType: '',
    status: '',
    executor: '',
    dateRange: null
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    setIsAdmin(userRole === 'ADMIN');
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: res } = await testApi.getExecutions();
      setData(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showDetail = async (record) => {
    setCurrentDetail(record);
    setDetailVisible(true);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      SUCCESS: { color: 'success', icon: <CheckCircleOutlined />, text: 'SUCCESS' },
      FAILED:  { color: 'error',   icon: <CloseCircleOutlined />, text: 'FAILED' },
      TIMEOUT: { color: 'warning', icon: <ClockCircleOutlined />, text: 'TIMEOUT' },
    };
    const config = statusConfig[status] || statusConfig.FAILED;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const TEST_TYPE_CONFIG = {
    ACCOUNT_CREATE: { color: 'purple',   icon: <UserAddOutlined /> },
    PAYIN:          { color: 'green',    icon: <DollarOutlined /> },
    PAYOUT:         { color: 'gold',     icon: <WalletOutlined /> },
    CARD_BIND:      { color: 'blue',     icon: <CreditCardOutlined /> },
    QUERY:          { color: 'cyan',     icon: <FileSearchOutlined /> },
  };

  const getTestTypeTag = (type) => {
    const cfg = TEST_TYPE_CONFIG[type] || { color: 'geekblue', icon: null };
    return <Tag color={cfg.color} icon={cfg.icon}>{type}</Tag>;
  };

  const columns = [
    { title: t('channel'), dataIndex: 'channelCode', width: 160,
      render: (channelCode) => {
        const logo = getChannelLogo(channelCode);
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <span style={{ fontSize: 15 }}>{logo.emoji}</span>
              <Tag color={logo.color} style={{ fontWeight: 500, margin: 0 }}>{logo.name}</Tag>
            </Space>
            <span style={{ fontSize: 11, color: '#9ca3af', paddingLeft: 2 }}>{channelCode}</span>
          </Space>
        );
      }
    },
    { title: t('testType'), dataIndex: 'testType', width: 130,
      render: (text) => getTestTypeTag(text)
    },
    { title: t('status'), dataIndex: 'status', width: 110, render: getStatusTag },
    { title: t('costTime'), dataIndex: 'costMs', width: 90,
      render: (ms) => (
        <Badge
          count={`${ms}ms`}
          color={ms > 5000 ? 'red' : ms > 2000 ? 'orange' : 'green'}
        />
      ),
      sorter: (a, b) => a.costMs - b.costMs
    },
    { title: t('executor'), dataIndex: 'executor', width: 100,
      render: (executor) => (
        <Space direction="vertical" size={2} style={{ alignItems: 'center' }}>
          <div style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#1890ff',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 'bold'
          }}>
            {executor?.charAt(0)?.toUpperCase()}
          </div>
          <span style={{ fontSize: 12, color: '#374151', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{executor}</span>
        </Space>
      )
    },
    { title: t('executionTime'), dataIndex: 'createTime', width: 110,
      render: (time) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: 13, color: '#374151' }}>{dayjs(time).format('YYYY-MM-DD')}</span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{dayjs(time).format('HH:mm:ss')}</span>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.createTime).unix() - dayjs(b.createTime).unix()
    },
    { title: t('operation'), width: 80, fixed: 'right', align: 'center',
      render: (_, record) => (
        <a
          onClick={() => showDetail(record)}
          style={{ color: '#667eea', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
        >
          <EyeOutlined style={{ marginRight: 4 }} />{t('detail')}
        </a>
      ),
    },
  ];

  const filteredData = data.filter(item => {
    return (
      (!filters.channelCode || item.channelCode === filters.channelCode) &&
      (!filters.testType || item.testType?.includes(filters.testType)) &&
      (!filters.status || item.status === filters.status) &&
      (!filters.executor || item.executor?.includes(filters.executor)) &&
      (!filters.dateRange || (
        dayjs(item.createTime).isSameOrAfter(filters.dateRange[0].startOf('day')) &&
        dayjs(item.createTime).isSameOrBefore(filters.dateRange[1].endOf('day'))
      ))
    );
  });

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: filteredData.length
    });
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card
          className="modern-card"
          title={
            <Space size={16}>
              <HistoryOutlined style={{ color: '#667eea', fontSize: 20 }} />
              <span style={{ fontSize: 20, fontWeight: 600 }}>{t('executionHistory')}</span>
              <Badge count={filteredData.length} color="#1890ff" />
            </Space>
          }
          extra={
            <Space>
              <Button icon={<DownloadOutlined />} type="text">{t('export')}</Button>
              <Button icon={<ReloadOutlined />} onClick={loadData} type="text">{t('refresh')}</Button>
            </Space>
          }
        >
          {/* 筛选区域 */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <div className="form-section-title">
              <FilterOutlined style={{ marginRight: 8, color: '#667eea' }} />
              {t('filterConditions')}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('channel')}</div>
                <Select
                  placeholder="全部"
                  value={filters.channelCode || undefined}
                  onChange={(value) => setFilters({...filters, channelCode: value || ''})}
                  allowClear
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  options={buildChannelOptions(Object.keys(channelLogos))}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('testType')}</div>
                <Select
                  placeholder="全部"
                  value={filters.testType}
                  onChange={(value) => setFilters({...filters, testType: value})}
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    { label: 'ACCOUNT_CREATE', value: 'ACCOUNT_CREATE' },
                    { label: 'PAYIN', value: 'PAYIN' },
                    { label: 'PAYOUT', value: 'PAYOUT' },
                    { label: 'CARD_BIND', value: 'CARD_BIND' },
                    { label: 'QUERY', value: 'QUERY' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('status')}</div>
                <Select
                  placeholder="全部"
                  value={filters.status}
                  onChange={(value) => setFilters({...filters, status: value})}
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    { label: <Tag color="success" icon={<CheckCircleOutlined />}>SUCCESS</Tag>, value: 'SUCCESS' },
                    { label: <Tag color="error"   icon={<CloseCircleOutlined />}>FAILED</Tag>,  value: 'FAILED' },
                    { label: <Tag color="warning" icon={<ClockCircleOutlined />}>TIMEOUT</Tag>, value: 'TIMEOUT' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ marginBottom: 4, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('timeRange')}</div>
                <RangePicker
                  placeholder={[t('startTime'), t('endTime')]}
                  value={filters.dateRange}
                  onChange={(dates) => setFilters({...filters, dateRange: dates})}
                  style={{ width: '100%' }}
                />
              </Col>
              {isAdmin && (
                <Col xs={24} sm={12} md={6}>
                  <div style={{ marginBottom: 4, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('executor')}</div>
                  <Input placeholder={t('pleaseInput', t('executor'))}
                    value={filters.executor}
                    onChange={(e) => setFilters({...filters, executor: e.target.value})}
                    allowClear
                    prefix={<SearchOutlined />}
                  />
                </Col>
              )}
            </Row>
          </div>

          <div className="modern-table">
            <Table
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              rowKey="executionId"
              scroll={{ x: 1000 }}
              pagination={{ 
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: filteredData.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => t('rangeRecords', range[0], range[1], total)
              }}
              onChange={handleTableChange}
              size="middle"
            />
          </div>
        </Card>

        <Modal
          title={
            <Space>
              <EyeOutlined style={{ color: '#667eea' }} />
              <span>{t('executionDetail')}</span>
              {currentDetail && getStatusTag(currentDetail.status)}
            </Space>
          }
          open={detailVisible}
          onCancel={() => setDetailVisible(false)}
          width={1200}
          footer={null}
          className="modern-modal"
        >
          {currentDetail && (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                <Descriptions.Item label={t('executionId')} span={3}>
                  <code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                    {currentDetail.executionId}
                  </code>
                </Descriptions.Item>
                <Descriptions.Item label={t('channel')}>
                  {(() => {
                    const logo = getChannelLogo(currentDetail.channelCode);
                    return (
                      <Space>
                        <span style={{ fontSize: 18 }}>{logo.emoji}</span>
                        <Tag color={logo.color} style={{ fontWeight: 500 }}>{logo.name}</Tag>
                      </Space>
                    );
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('testType')}>
                  {getTestTypeTag(currentDetail.testType)}
                </Descriptions.Item>
                <Descriptions.Item label={t('status')}>
                  {getStatusTag(currentDetail.status)}
                </Descriptions.Item>
                <Descriptions.Item label={t('costTime')}>
                  <Badge count={`${currentDetail.costMs}ms`} color={currentDetail.costMs > 5000 ? 'red' : 'green'} />
                </Descriptions.Item>
                <Descriptions.Item label={t('httpStatus')}>
                  <Tag color={currentDetail.httpStatus === 200 ? 'success' : 'error'}>{currentDetail.httpStatus}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('traceId')} span={2}>
                  <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                    {currentDetail.traceId}
                  </code>
                </Descriptions.Item>
                <Descriptions.Item label={t('channelOrderId')} span={3}>
                  {currentDetail.channelOrderId ? (
                    <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                      {currentDetail.channelOrderId}
                    </code>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('executor')}>
                  <Space>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#1890ff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold'
                    }}>
                      {currentDetail.executor?.charAt(0)?.toUpperCase()}
                    </div>
                    {currentDetail.executor}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('executionTime')} span={2}>
                  {dayjs(currentDetail.createTime).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              </Descriptions>

              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <Card title={`📤 ${t('requestParams')}`} size="small" className="json-viewer">
                    <ReactJson 
                      src={JSON.parse(currentDetail.requestBody || '{}')} 
                      collapsed={1}
                      theme="rjv-default"
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={true}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title={`📥 ${t('responseResult')}`} size="small" className="json-viewer">
                    <ReactJson 
                      src={JSON.parse(currentDetail.responseBody || '{}')} 
                      collapsed={1}
                      theme="rjv-default"
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={true}
                    />
                  </Card>
                </Col>
              </Row>
            </Space>
          )}
        </Modal>
      </div>
    </div>
  );
}
